import React from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { NotepadText, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import type { PublicAssessment } from '@arrowise/shared';
import { useAuth } from '@/providers/AuthProvider';
import AssessmentListItem from './AssessmentListItem';

interface AssessmentListProps {
    assessments: PublicAssessment[];
    loading?: boolean;
    onAssessClick?: () => void;
    currentUserId?: string;
    teamId?: string;
    onEditAssessment?: (assessment: PublicAssessment) => void;
    onDeleteAssessment?: (assessment: PublicAssessment) => void;
    isGraded?: boolean;
}

const AssessmentList: React.FC<AssessmentListProps> = ({
    assessments,
    loading,
    onAssessClick,
    currentUserId,
    teamId,
    onEditAssessment,
    onDeleteAssessment,
    isGraded = false
}) => {
    const { getUserTeamRole, isTeamOwner } = useAuth();
    const hasUserAssessment = currentUserId ? assessments.some(assessment => assessment.assessorId === currentUserId) : false;

    // Check if user is admin or owner in team context
    const isAdminOrOwner = teamId ? (isTeamOwner(teamId) || getUserTeamRole(teamId) === 'admin') : false;

    if (loading) {
        return (
            <div className="space-y-4">
                {Array.from({ length: 2 }).map((_, i) => (
                    <Card key={i}>
                        <CardHeader>
                            <Skeleton className="h-4 w-1/3" />
                            <Skeleton className="h-3 w-1/4" />
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-2">
                                <Skeleton className="h-3" />
                                <Skeleton className="h-3 w-3/4" />
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>
        );
    }

    if (assessments.length === 0) {
        const isTeamContext = !!teamId;
        const shouldShowGenericMessage = !isTeamContext || isAdminOrOwner;

        return (
            <div className="text-center py-8">
                <NotepadText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">
                    {isGraded
                        ? 'Session has been assessed'
                        : (shouldShowGenericMessage ? 'No assessments yet' : 'You have no assessments yet')
                    }
                </h3>
                <p className="text-muted-foreground mb-4">
                    {isGraded
                        ? 'This session has been marked as graded and assessments are no longer available.'
                        : (shouldShowGenericMessage
                            ? 'Be the first to assess this candidate.'
                            : 'Create your assessment for this candidate.'
                        )
                    }
                </p>
                {onAssessClick && !isGraded && (
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                onClick={onAssessClick}
                                className="flex items-center justify-center gap-2 mx-auto"
                                disabled={hasUserAssessment}
                            >
                                <Pencil className="w-4 h-4" />
                                Assess
                            </Button>
                        </TooltipTrigger>
                        {hasUserAssessment && (
                            <TooltipContent>
                                <p>You can edit your existing assessment or delete it</p>
                            </TooltipContent>
                        )}
                    </Tooltip>
                )}
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Show assess button if user doesn't have an assessment */}
            {onAssessClick && !hasUserAssessment && !isGraded && (
                <div className="flex justify-center mt-3 mb-6">
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                onClick={onAssessClick}
                                className="flex items-center justify-center gap-2"
                            >
                                <Pencil className="w-4 h-4" />
                                Add assessment
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p>Create your assessment for this candidate</p>
                        </TooltipContent>
                    </Tooltip>
                </div>
            )}

            {assessments.map((assessment) => (
                <AssessmentListItem
                    key={assessment.id}
                    assessment={assessment}
                    currentUserId={currentUserId}
                    teamId={teamId}
                    onEdit={onEditAssessment}
                    onDelete={onDeleteAssessment}
                    isGraded={isGraded}
                />
            ))}
        </div>
    );
};

export default AssessmentList;
